import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchActiveLeaderCount,
  fetchCoordination,
  fetchCoordinations,
  fetchMinistriesWithLeaders,
  fetchMinistry,
  fetchMyScopeBindings,
  fetchProjectStatusCounts,
} from '../api/org.api';
import { queryKeys } from '@/shared/lib/queryKeys';
import type {
  Coordination,
  Id,
  MinistryWithCoordination,
  MinistryWithLeader,
  ProjectStatusCount,
  ScopeBinding,
} from '@/shared/types/domain';

/**
 * A estrutura muda quando a secretaria cadastra um ministério — o que
 * acontece algumas vezes por ano, não por minuto. `staleTime` longo evita
 * refetch a cada troca de tela sem custo nenhum de frescor.
 */
const ORG_STALE_TIME = 5 * 60_000;

export function useCoordinations(): UseQueryResult<Coordination[]> {
  return useQuery({
    queryKey: queryKeys.coordinations.all,
    queryFn: fetchCoordinations,
    staleTime: ORG_STALE_TIME,
  });
}

export function useCoordination(id: Id | undefined): UseQueryResult<Coordination | null> {
  return useQuery({
    queryKey: queryKeys.coordinations.detail(id ?? ''),
    queryFn: () => fetchCoordination(id as Id),
    enabled: Boolean(id),
    staleTime: ORG_STALE_TIME,
  });
}

/** Sem `coordinationId`, traz todos os ministérios ao alcance da pessoa. */
export function useMinistriesWithLeaders(
  coordinationId?: Id,
): UseQueryResult<MinistryWithLeader[]> {
  return useQuery({
    queryKey: coordinationId
      ? queryKeys.ministries.byCoordination(coordinationId)
      : queryKeys.ministries.all,
    queryFn: () => fetchMinistriesWithLeaders(coordinationId),
    staleTime: ORG_STALE_TIME,
  });
}

/**
 * Os vínculos vigentes da pessoa — a seção "Minha área" da sidebar.
 *
 * Mesmo `staleTime` do resto da feature: vínculo é cadastro de secretaria,
 * não estado de esteira. Quem o derruba de verdade é a invalidação de
 * `queryKeys.me`, que alcança esta chave por prefixo.
 */
export function useMyScopeBindings(profileId: Id | undefined): UseQueryResult<ScopeBinding[]> {
  return useQuery({
    queryKey: queryKeys.myScopeBindings(profileId ?? ''),
    queryFn: () => fetchMyScopeBindings(profileId as Id),
    enabled: Boolean(profileId),
    staleTime: ORG_STALE_TIME,
  });
}

export function useMinistry(id: Id | undefined): UseQueryResult<MinistryWithCoordination | null> {
  return useQuery({
    queryKey: queryKeys.ministries.detail(id ?? ''),
    queryFn: () => fetchMinistry(id as Id),
    enabled: Boolean(id),
    staleTime: ORG_STALE_TIME,
  });
}

/**
 * Quantas pessoas respondem por uma coordenação ou ministério hoje.
 *
 * Mesmo `staleTime` do resto da feature, e pela mesma razão: quem muda este
 * número é a secretaria cadastrando ou encerrando um vínculo, não a esteira.
 */
export function useActiveLeaderCount(): UseQueryResult<number> {
  return useQuery({
    queryKey: queryKeys.activeLeaderCount,
    queryFn: fetchActiveLeaderCount,
    staleTime: ORG_STALE_TIME,
  });
}

/**
 * Contagem por situação de TODO o escopo da pessoa, numa consulta só.
 *
 * `staleTime` curto, ao contrário da estrutura: contagem muda a cada
 * transição da esteira, e é o número que a liderança olha primeiro. Quem
 * invalida de verdade é `invalidateAfterTransition`; isto é só a rede de
 * segurança para a aba que ficou aberta enquanto outra pessoa aprovava algo.
 */
export function useProjectStatusCounts(): UseQueryResult<ProjectStatusCount[]> {
  return useQuery({
    queryKey: queryKeys.projectStatusCounts,
    queryFn: fetchProjectStatusCounts,
    staleTime: 30_000,
  });
}
